'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Calendar, MapPin, Users } from 'lucide-react';
import CreateEventModal from '@/components/ui/CreateEventModal';
import { HiddenListGate } from '@/components/ui';
import { useLanguage } from '@/context/LanguageContext';
import { formatTime12H, parseDateOnly } from '@/utils';
import { fetchStrapiCollection } from '@/lib/strapi';

interface GroupEventListItem {
  id: number;
  documentId: string;
  title: string;
  date: string;
  timeStart: string;
  timeEnd: string;
  location: string;
  attendees?: { id: number }[];
  attendeeCount?: number;
  openToPublic?: boolean;
  createdAt: string;
}

interface EventsTabProps {
  groupId: number;
  isMember?: boolean;
  isSiteAdmin?: boolean;
  canCreate: boolean;
  eventListVisible?: boolean;
}

export function EventsTab({
  groupId,
  isMember = false,
  isSiteAdmin = false,
  canCreate,
  eventListVisible = true,
}: EventsTabProps) {
  const router = useRouter();
  const { t, language } = useLanguage();
  const [events, setEvents] = useState<GroupEventListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const allEvents = await fetchStrapiCollection<GroupEventListItem>('events', {
        filters: { group: { id: { $eq: groupId } }, deletedAt: { $null: true } },
        populate: { attendees: { fields: ['id'] } },
        sort: 'date:asc',
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day

      // Filter only future events (today and onwards)
      const futureEvents = allEvents.filter((item) => parseDateOnly(item.date) >= today);

      setEvents(futureEvents.map((item) => ({
        id: item.id,
        documentId: item.documentId,
        title: item.title,
        date: item.date,
        timeStart: item.timeStart,
        timeEnd: item.timeEnd,
        location: item.location,
        attendees: item.attendees || [],
        attendeeCount: item.attendeeCount,
        openToPublic: item.openToPublic,
        createdAt: item.createdAt,
      })));
    } catch (err) {
      console.error('Error fetching events:', err);
    }
    setLoading(false);
  }, [groupId]);

  useEffect(() => {
    async function loadEvents() {
      await fetchEvents();
    }

    void loadEvents();
  }, [fetchEvents]);

  const formatEventDate = (dateStr: string) => {
    return parseDateOnly(dateStr).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Site admins see private events for any group, just like group members
  // (MWBI-592) — they need not have joined the group themselves.
  const canSeeAllEvents = isMember || isSiteAdmin;
  const visibleEvents = canSeeAllEvents
    ? events
    : events.filter((event) => event.openToPublic !== false);
  const canViewEvents = canSeeAllEvents || eventListVisible;

  if (loading) return <div className="text-center py-12 text-foreground-muted">{t.common.loading}</div>;

  return (
    <>
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        preselectedGroupId={groupId}
        onCreated={(documentId) => router.push(`/events/${documentId}`)}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground" data-testid="group-events-tab-heading">{t.groupTabs.upcomingEvents} ({events.length})</h2>
          {/* MWBI-890 — the in-tab visibility button was removed; event
              visibility is now set only on the Edit Group form. */}
          <div className="flex items-center gap-2">
            {canCreate && (
              <button
                onClick={() => setShowCreateModal(true)}
                data-testid="group-events-tab-create-event-btn"
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
              >
                {t.events.createEvent}
              </button>
            )}
          </div>
        </div>

      {!canViewEvents ? (
        // MWBI-890 — Forum-style gate. The skeletons below are empty divs, not
        // the fetched events: a non-member must not receive the rows at all.
        <HiddenListGate
          title={t.groupTabs.hiddenEventsTitle}
          message={t.groupTabs.onlyMembersCanViewEvents}
          testIdPrefix="group-events-tab"
        >
          <div className="space-y-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="rounded-lg border border-border bg-background-secondary p-4 h-24" />
            ))}
          </div>
        </HiddenListGate>
      ) : visibleEvents.length === 0 ? (
        <div data-testid="group-events-tab-empty-state" className="text-center py-12 text-foreground-muted border border-border rounded-lg bg-background-secondary">
          <Calendar size={32} className="mx-auto mb-2 opacity-50" />
          <p>{t.events.noUpcomingEvents}</p>
          {canCreate && <p className="text-sm mt-1">{t.groupTabs.checkBackLater}</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleEvents.map((event) => (
            <Link key={event.documentId} href={`/events/${event.documentId}`} data-testid={`group-events-tab-card-${event.documentId}`} className="block p-4 border border-border rounded-lg bg-background hover:bg-background-secondary transition">
              <div className="flex flex-col gap-3">
                <div className="min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-medium text-foreground hover:text-primary">{event.title}</h3>
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background-secondary px-2.5 py-1 text-xs font-medium text-foreground-muted whitespace-nowrap">
                      <Users size={12} />
                      {event.attendeeCount ?? event.attendees?.length ?? 0} {t.events.attending}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 text-sm text-foreground-muted mt-1">
                    <span className="flex items-center gap-1" data-testid="group-events-tab-event-time"><Calendar size={12} /> {formatEventDate(event.date)} • {formatTime12H(event.timeStart)} - {formatTime12H(event.timeEnd)}</span>
                    <span className="flex items-center gap-1"><MapPin size={12} /> {event.location}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      </div>
    </>
  );
}

